import { useEffect, useState } from 'react';
import { useRows } from '@/hooks/useLive';
import { save, softDelete, upsert } from '@/lib/crud';
import { hours as fmtHours, isoDate } from '@/lib/format';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { ConfirmRemove } from '@/components/ui/ConfirmRemove';
import {
  CheckboxInput, DateTimeInput, NumberInput, SelectInput, TextArea, TextInput,
} from '@/components/ui/Field';

interface WeatherEvent extends Record<string, unknown> {
  id: string;
  event_date: string;
  started_at: string;
  ended_at: string | null;
  hours_lost: number | null;
  weather_type_id: string | null;
  affected_area: string | null;
  affects_whole_site: boolean;
  production_lost_t: number | null;
  comments: string | null;
}


/**
 * Weather downtime is a separate register on purpose. Booked as a machine
 * fault it would drag availability down and hide the real mechanical
 * picture; the availability calculation excludes it by default.
 */
export function WeatherDrawer({
  event, onClose, locked,
}: { event: Partial<WeatherEvent>; onClose: () => void; locked?: string | null }) {
  const isNew = !event.id;
  const types = useRows<Record<string, unknown>>('weather_types', 'sort_order');

  const [v, setV] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setV({
      event_date: isoDate(),
      started_at: new Date().toISOString(),
      affects_whole_site: true,
      ...event,
    });
  }, [event]);

  const set = (k: string, val: unknown) => setV((d) => ({ ...d, [k]: val }));

  const lost = v.started_at && v.ended_at
    ? (new Date(String(v.ended_at)).getTime() - new Date(String(v.started_at)).getTime()) / 3_600_000
    : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!v.event_date) e.event_date = 'Date is required';
    if (!v.started_at) e.started_at = 'Start time is required';
    if (v.ended_at && String(v.ended_at) < String(v.started_at)) {
      e.ended_at = 'End is before the start';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setBusy(true);
    const payload = { ...v };
    if (event.id) payload.id = event.id;
    const ok = await save(
      () => upsert('weather_events', payload as Record<string, unknown> & { id?: string }),
      'Weather downtime saved',
    );
    setBusy(false);
    if (ok) onClose();
  }

  return (
    <Drawer
      open
      locked={locked}
      width={540}
      title={isNew ? 'Log weather downtime' : 'Weather downtime'}
      subtitle="Recorded separately — never counted as a machine failure"
      onClose={onClose}
      footer={
        <>
          {!isNew && (
            <ConfirmRemove
              disabled={busy}
              label="Remove"
              question="Remove this weather event?"
              onConfirm={async () => {
                const ok = await save(() => softDelete('weather_events', event.id as string), 'Removed');
                // Close only if it went through. A refusal leaves the record
                // open with the reason on screen, rather than closing over it.
                if (ok !== null) onClose();
                return ok !== null;
              }}
            />
          )}
          <span className="spacer" />
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} loading={busy}>Save</Button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput id="w-date" label="Date" type="date" required error={errors.event_date}
          value={String(v.event_date ?? '')} onChange={(x) => set('event_date', x)} />
        <SelectInput id="w-type" label="Weather" value={(v.weather_type_id as string) ?? null}
          options={(types ?? []).map((t) => ({ value: String(t.id), label: String(t.name) }))}
          onChange={(x) => set('weather_type_id', x)} />

        <DateTimeInput id="w-start" label="Stopped work at" required error={errors.started_at}
          value={(v.started_at as string) ?? null}
          onChange={(x) => set('started_at', x)} />
        <DateTimeInput id="w-end" label="Resumed at" error={errors.ended_at}
          value={(v.ended_at as string) ?? null}
          onChange={(x) => set('ended_at', x)} />

        <div className="field--full">
          <div className="engine-hours">
            <span className="engine-hours__value">{lost == null ? '—' : fmtHours(lost)}</span>
            <span className="engine-hours__label">hours lost</span>
          </div>
        </div>

        <TextInput id="w-area" label="Affected area" value={String(v.affected_area ?? '')}
          onChange={(x) => set('affected_area', x || null)}
          help="Leave blank if the whole site stopped." />
        <NumberInput id="w-lost" label="Production lost" suffix="t" min={0} step={10}
          value={(v.production_lost_t as number) ?? null}
          onChange={(x) => set('production_lost_t', x)}
          help="Estimate — it is what turns lost hours into a tonnage the manager recognises." />

        <div className="field--full">
          <CheckboxInput id="w-site" label="The whole site was affected"
            value={Boolean(v.affects_whole_site)} onChange={(x) => set('affects_whole_site', x)} />
        </div>

        <div className="field--full">
          <TextArea id="w-comments" label="Comments" value={String(v.comments ?? '')}
            onChange={(x) => set('comments', x || null)} />
        </div>
      </div>
    </Drawer>
  );
}
